import * as React from "react";
import { FullScreenDialog } from "../FullScreenDialog";
import { Paper } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import { AddPaymentForm, PaymentDialogState } from "../Forms/AddPaymentForm";
import { Contact } from "../../generated/nest-graphql";
import { AreYouSureDialog } from "../Items/AreYouSureDialog";
import { useDispatch } from "../../contexts/snackbar-context";
import { showWarningAlert } from "../../actions";

export const AddPaymentsDialog: React.FC<{
  amount: string;
  balanceDue: string;
  invoiceId: string;
  contactId: string;
  laborCost: string;
  partsCost: string;
  partsTax: string;
  laborTax: string;
  payer: string;
  totalTax: string;
  subTotal: string;
  open: boolean;
  onClose: () => void;
  openInspection: () => void;
  refetch: any;
  jobId: string;
  checkListsCreated: boolean;
  contact: Contact;
  amountPaid: string;
  status: string;
}> = ({
  laborCost,
  partsCost,
  partsTax,
  laborTax,
  subTotal,
  totalTax,
  amount,
  balanceDue,
  payer,
  invoiceId,
  contactId,
  open,
  onClose,
  refetch,
  jobId,
  checkListsCreated,
  contact,
  amountPaid,
  status,
  openInspection,
}) => {
  const dispatch = useDispatch();
  // A ref: closeDialog reads it after an await and needs the current value.
  const paymentState = React.useRef<PaymentDialogState>({ inProgress: false, balanceMayBeStale: false });
  const reportPaymentState = React.useCallback((state: PaymentDialogState) => {
    paymentState.current = state;
  }, []);
  const [confirmClose, setConfirmClose] = React.useState<null | "inProgress" | "staleBalance">(null);

  const closeDialog = async ({ force = false } = {}) => {
    setConfirmClose(null);
    try {
      //refetch invoice data so that in case of partial payment the appropriate values are reflected
      await refetch({
        id: invoiceId,
      });
    } catch (e) {
      // The balance may be stale and couldn't refresh: ask rather than reopen on it.
      if (!force && paymentState.current.balanceMayBeStale) {
        setConfirmClose("staleBalance");
        return;
      }
      showWarningAlert(dispatch, "Couldn't refresh the invoice. Reload it before taking another payment.");
    }
    // A payment started while refreshing; don't close underneath it.
    if (!force && paymentState.current.inProgress) return;
    onClose();
  };

  return (
    <FullScreenDialog
      title={"Add Payment"}
      open={open}
      onClose={() => {
        // Closing drops the form's lock (Cash/Check could then go on top of a card charge), so confirm.
        if (paymentState.current.inProgress) {
          setConfirmClose("inProgress");
          return;
        }
        closeDialog();
      }}
    >
      <AreYouSureDialog
        title={"Close while a payment is still processing?"}
        subTitle={
          "The customer may already have been charged. Closing and taking another payment could charge them twice."
        }
        open={confirmClose === "inProgress"}
        onNo={() => setConfirmClose(null)}
        onClose={() => setConfirmClose(null)}
        onYes={() => closeDialog({ force: true })}
      />
      <AreYouSureDialog
        title={"Close without the updated balance?"}
        subTitle={
          "The balance couldn't refresh, so Get Paid may show an outdated amount until the invoice reloads. Reload the invoice before collecting payment."
        }
        open={confirmClose === "staleBalance"}
        onNo={() => setConfirmClose(null)}
        onClose={() => setConfirmClose(null)}
        onYes={() => {
          setConfirmClose(null);
          onClose();
        }}
      />
      <Paper>
        <Container className={"py-4"}>
          <AddPaymentForm
            laborCost={laborCost}
            partsCost={partsCost}
            partsTax={partsTax}
            payer={payer}
            laborTax={laborTax}
            subTotal={subTotal}
            totalTax={totalTax}
            amount={amount}
            balanceDue={balanceDue}
            invoiceId={invoiceId}
            contactId={contactId}
            refetch={refetch}
            onClose={onClose}
            jobId={jobId}
            checkListsCreated={checkListsCreated}
            contact={contact}
            amountPaid={amountPaid}
            status={status}
            openInspection={openInspection}
            onPaymentStateChange={reportPaymentState}
          />
        </Container>
      </Paper>
    </FullScreenDialog>
  );
};
